<template>
  <div class="investor-statistics">
    <h1 class="headline">{{ $t('workersStatistics.title') }}</h1>

    <div class="investor-statistics__filters">
      <v-menu
        ref="menu"
        v-model="dateMenu"
        :close-on-content-click="false"
        :return-value.sync="filters.date"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            class="investor-statistics__date-field"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
            :label="$t('common.monthAndYear')"
            :value="filters.date.replace('-', '/')"
          />
        </template>
        <v-date-picker
          v-model="filters.date"
          color="warning"
          class="investor-statistics__date"
          type="month"
          no-title
          :locale="$i18n.locale"
          @change="
            () => {
              $refs.menu.save(filters.date);
            }
          "
        >
        </v-date-picker>
      </v-menu>
    </div>

    <v-data-table
      ref="statistic-table"
      class="investor-statistics__table"
      hide-default-header
      hide-default-footer
      :items-per-page="-1"
      :loading="loading"
      :height="tableHeight"
    >
      <template #header>
        <thead class="v-data-table-header">
          <tr>
            <th class="park">
              <div>{{ $t('employees.park') }}</div>
            </th>
            <th class="number">
              <div>
                {{ $t('auto.fields.number') }}
              </div>
            </th>
            <th v-for="day in currentMonthLastDay" :key="day">
              <b class="day">{{ day }}</b>
            </th>
          </tr>
        </thead>
      </template>

      <template #body>
        <tbody>
          <template v-if="Object.keys(currentDateList).length">
            <tr
              v-for="(data, number) in currentDateList"
              :key="number"
              :class="{ 'first-line': data.park }"
            >
              <td
                v-if="data.park"
                :rowspan="data.rowspan"
                style="vertical-align:middle"
                class="park"
              >
                <b>{{ data.park }}</b>
              </td>
              <td class="number">
                <b>{{ number }}</b>
              </td>
              <td
                v-for="day in currentMonthLastDay"
                :key="day"
                :class="{ 'grey-cell': day % 2 === 0 }"
                v-html="getDayStatuses(day, data)"
              ></td>
            </tr>

            <tr
              v-if="Object.keys(total).length"
              class="investor-statistics__table-footer"
            >
              <td colspan="2">
                <b> {{ $t('common.total') }}: </b>

                <ul class="investor-statistics__table-total">
                  <li v-for="(count, status) in total" :key="status">
                    {{ status }} — {{ count }}
                  </li>
                </ul>
              </td>
              <td :colspan="currentMonthLastDay"></td>
            </tr>
          </template>

          <tr v-else>
            <td :colspan="currentMonthLastDay + 2" align="center">
              <b>Нет данных</b>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import calcTableHeight from '@/helpers/calcTableHeight';

export default {
  data: () => ({
    loading: false,
    dateMenu: false,
    tableHeight: 'auto',
    filters: {
      date: '',
    },
  }),
  watch: {
    filters: {
      deep: true,
      async handler(params) {
        this.loading = true;
        await this.$store.dispatch('investor-board/fetchStatistics', params);
        this.loading = false;
      },
    },
  },
  computed: {
    ...mapState({
      list: (state) => state['investor-board'].statistics.list,
      total: (state) => state['investor-board'].statistics.total,
    }),
    currentMonthLastDay() {
      const currentMonth = new Date(this.filters.date);
      const lastDay = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        0,
      );

      return lastDay.getDate();
    },
    currentDateList() {
      const data = this.list[this.filters.date];
      if (!data) return {};

      return Object.entries(data).reduce((list, [park, autosList]) => {
        const numbersList = Object.keys(autosList);
        const autoData = numbersList.reduce((autos, number, idx) => {
          /* eslint-disable */
          autos[number] = autosList[number];

          if (idx === 0) {
            autos[number].park = park;
            autos[number].rowspan = numbersList.length;
          }

          return autos;
          /* eslint-enable */
        }, {});

        return {
          ...list,
          ...autoData,
        };
      }, {});
    },
  },
  methods: {
    getDayStatuses(day, data) {
      const key = Object.keys(data).find((num) => Number(num) === day);

      if (!key) return '-';

      const list = data[key].map((status) => `<li>${status}</li>`);
      return `<ul class="investor-statistics__status-list">${list.join(
        '',
      )}</ul>`;
    },
  },
  created() {
    const today = new Date();
    let month = today.getMonth() + 1;
    month = month < 10 ? `0${month}` : month;

    this.filters.date = `${today.getFullYear()}-${month}`;
  },
  mounted() {
    const table = this.$refs['statistic-table'];
    this.tableHeight = calcTableHeight(table.$el) - 50;
  },
};
</script>

<style lang="scss">
.investor-statistics {
  width: 100%;

  .headline {
    margin-top: 10px;
  }

  &__date {
    .v-btn--text {
      text-decoration: none !important;
      padding: inherit !important;

      &::before {
        background-color: #333 !important;
      }
    }
  }

  &__date-field {
    max-width: 200px;
  }

  &__status-list {
    padding: 0 !important;

    li {
      list-style: none;
      font-size: 12px !important;
    }
  }

  &__table {
    .v-data-table-header {
      position: sticky;
      top: 0;
      z-index: 2;
    }

    tr {
      background-color: #fff !important;

      td {
        border-bottom: none !important;
        border-top: thin solid rgba(0, 0, 0, 0.12);
      }

      &.first-line:not(:first-child) td {
        margin-top: -1px;
        border-top: 1.5px dashed #464646;
      }
    }

    td {
      padding-top: 5px;
      padding-bottom: 5px;
    }

    th,
    td:not(.park) {
      white-space: nowrap;
    }

    th:nth-child(2) {
      border-right: 2px solid #dddddd;
    }

    th:nth-child(n + 3) {
      border-right: 1px solid #dddddd;
    }

    th.park,
    td.park {
      position: sticky;
      left: 0;

      > * {
        display: block;
        width: 65px;
        word-break: break-word;
      }
    }

    th.number,
    td.number {
      position: sticky;
      left: 97px;

      > * {
        display: block;
        width: 85px;
        word-break: break-word;
      }
    }

    td.park,
    td.number {
      background-color: #f8f8f8;
    }

    td.number {
      border-right: 2px solid #dddddd;
    }

    td.grey-cell {
      background-color: #dcdcdc;
    }

    .day {
      font-size: 14px;
    }

    &-footer {
      position: sticky;
      bottom: 0;
      left: 0;

      td {
        padding-top: 5px;
        padding-bottom: 10px;
        background-color: #fff !important;
        border-top: 2px solid #dddddd;

        &:first-child {
          position: sticky;
          left: 0;
          z-index: 1;
        }
      }
    }

    &:nth-child(2) {
      position: static;
    }

    &-total {
      padding: 0 !important;
      margin-top: 10px;

      li {
        list-style: none;
      }
    }
  }
}
</style>
